import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A trench gas alarm: an empty brass shell casing hung from a small timber
// gallows frame, struck with the rod hanging beside it. The casing (and its
// hanging cord) is named "casing" so the one-shot "ring" animation can swing
// it a few degrees about the point it hangs from.
const TIMBER = "#8b6438";
const DEAD = "#4f4238";
const BRASS = "#b08d3a";
const CORD = "#4f4238";
const IRON = "#55534e";

const POST_X = -0.18; // upright of the gallows
const ARM_Y = 1.06; // underside of the horizontal arm
const HANG_X = 0.1; // where the casing hangs
const ROD_X = 0.3; // where the striker rod hangs

export const gasAlarm = defineModel({
  id: "gas-alarm",
  batchId: "wwi",
  author: "Fable 5",
  name: "Gas Alarm",
  tags: ["wwi", "prop", "trench", "alarm", "animated"],
  summary: "A brass shell casing hung from a timber gallows, with a striker rod beside it.",
  footprint: 0.35,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // Timber gallows frame: two ground feet, an upright, a brace, an arm
      { geometry: new THREE.BoxGeometry(0.4, 0.06, 0.09), color: DEAD, position: [POST_X, 0.03, 0], rotation: [0, Math.PI / 2, 0] },
      { geometry: new THREE.BoxGeometry(0.07, 1.1, 0.07), color: TIMBER, position: [POST_X, 0.6, 0] },
      { geometry: new THREE.BoxGeometry(0.55, 0.07, 0.07), color: TIMBER, position: [POST_X + 0.24, ARM_Y + 0.035, 0] },
      { geometry: new THREE.BoxGeometry(0.05, 0.3, 0.05), color: TIMBER,
        position: [POST_X + 0.12, ARM_Y - 0.11, 0], rotation: [0, 0, 0.85] },
      // Hanging cord + brass shell casing (named, so "ring" can swing them)
      { geometry: new THREE.CylinderGeometry(0.008, 0.008, 0.1, 6), color: CORD,
        position: [HANG_X, ARM_Y - 0.05, 0], name: "casing" },
      { geometry: new THREE.CylinderGeometry(0.065, 0.06, 0.34, 10), color: BRASS,
        position: [HANG_X, ARM_Y - 0.27, 0], name: "casing" },
      { geometry: new THREE.CylinderGeometry(0.072, 0.072, 0.035, 10), color: BRASS,
        position: [HANG_X, ARM_Y - 0.42, 0], name: "casing" },
      // Striker rod hanging beside the casing on its own cord
      { geometry: new THREE.CylinderGeometry(0.007, 0.007, 0.14, 6), color: CORD,
        position: [ROD_X, ARM_Y - 0.07, 0] },
      { geometry: new THREE.CylinderGeometry(0.015, 0.015, 0.3, 8), color: IRON,
        position: [ROD_X, ARM_Y - 0.29, 0] },
    ];
  },
  animations: [
    {
      id: "ring",
      label: "Ring",
      duration: 1.6,
      loop: false,
      tracks: [
        {
          // A struck, damped swing about the point where the cord meets the arm.
          target: "casing",
          pivot: [HANG_X, ARM_Y, 0],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.25, value: [0, 0, 0.28], ease: "easeOut" },
            { t: 0.6, value: [0, 0, -0.2], ease: "easeInOut" },
            { t: 0.95, value: [0, 0, 0.12], ease: "easeInOut" },
            { t: 1.3, value: [0, 0, -0.05], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeOut" },
          ],
        },
      ],
    },
  ],
});
